import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Pencil, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { EmptyState } from '@/components/common/EmptyState';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useServicos, useToggleServicoAtivo, useExcluirServico } from '@/hooks/useServicos';
import { ServicoFormDialog } from '@/components/servicos/ServicoFormDialog';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { formatarDescricaoPreco, getTipoPrecoLabels } from '@/types/servico';
import type { Servico } from '@/types/servico';

export default function Servicos() {
  useFeatureTracking('servicos');
  const { t } = useTranslation();
  const TIPO_PRECO_LABELS = getTipoPrecoLabels(t);

  const { data: servicos, isLoading } = useServicos();
  const toggleAtivo = useToggleServicoAtivo();
  const excluirServico = useExcluirServico();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [servicoParaEditar, setServicoParaEditar] = useState<Servico | undefined>();
  const [servicoParaExcluir, setServicoParaExcluir] = useState<Servico | undefined>();
  const [termoBusca, setTermoBusca] = useState('');

  const servicosFiltrados =
    servicos?.filter(
      (s) =>
        s.nome.toLowerCase().includes(termoBusca.toLowerCase()) ||
        s.descricao?.toLowerCase().includes(termoBusca.toLowerCase()),
    ) || [];

  const handleEditar = (servico: Servico) => {
    setServicoParaEditar(servico);
    setDialogOpen(true);
  };

  const handleNovo = () => {
    setServicoParaEditar(undefined);
    setDialogOpen(true);
  };

  const handleConfirmarExclusao = () => {
    if (servicoParaExcluir) {
      excluirServico.mutate(servicoParaExcluir.id, {
        onSettled: () => setServicoParaExcluir(undefined),
      });
    }
  };

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('services.title')}
          acoes={
            <>
              <button
                type="button"
                className="ow-btn ow-btn--primary ow-btn--sm"
                onClick={handleNovo}
              >
                <Plus size={14} />
                {t('services.newService')}
              </button>
              <UserMenu />
            </>
          }
        />

        <div className="dst-toolbar">
          <label className="dst-toolbar__search">
            <Search size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
            <input
              type="text"
              placeholder={t('services.searchPlaceholder')}
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
          </label>
          <span className="dst-toolbar__count">
            {servicosFiltrados.length} {t('common.of', 'de')} {servicos?.length || 0}
          </span>
        </div>

        {isLoading ? (
          <div className="tbl-card">
            <table className="tbl">
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td>
                      <Skeleton className="h-5 w-40" />
                    </td>
                    <td>
                      <Skeleton className="h-5 w-24" />
                    </td>
                    <td>
                      <Skeleton className="h-5 w-20" />
                    </td>
                    <td>
                      <Skeleton className="h-5 w-16" />
                    </td>
                    <td>
                      <Skeleton className="h-5 w-20" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : servicosFiltrados.length === 0 ? (
          <EmptyState
            titulo={termoBusca ? t('services.noServicesFound') : t('services.noServices')}
            descricao={
              termoBusca
                ? t('services.noServicesFoundDescription')
                : t('services.noServicesDescription')
            }
            acao={
              !termoBusca && (
                <Button onClick={handleNovo} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('services.addService')}
                </Button>
              )
            }
          />
        ) : (
          <div className="tbl-card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('services.priceTypeLabel', 'Tipo de preço')}</th>
                  <th className="num">{t('services.priceLabel', 'Preço')}</th>
                  <th>{t('common.status')}</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {servicosFiltrados.map((servico) => (
                  <tr key={servico.id} style={{ cursor: 'default' }}>
                    <td>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-medium truncate">{servico.nome}</span>
                        {servico.descricao && (
                          <span
                            className="text-xs text-muted-foreground truncate max-w-[340px]"
                            title={servico.descricao}
                          >
                            {servico.descricao}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="status-pill" style={{ padding: '2px 8px', fontSize: 10 }}>
                        {TIPO_PRECO_LABELS[servico.tipo_preco]}
                      </span>
                    </td>
                    <td className="num t-currency">{formatarDescricaoPreco(servico, t)}</td>
                    <td>
                      <span
                        className={`status-pill ${
                          servico.ativo ? 'status-pill--ink' : 'status-pill--soft'
                        }`}
                      >
                        <span className="status-pill__led" aria-hidden />
                        {servico.ativo ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end items-center">
                        <Switch
                          checked={servico.ativo}
                          onCheckedChange={() =>
                            toggleAtivo.mutate({ id: servico.id, ativo: !servico.ativo })
                          }
                          disabled={toggleAtivo.isPending}
                          aria-label={t('common.toggleActive', 'Toggle activo')}
                        />
                        <button
                          type="button"
                          className="painel__icon-btn"
                          onClick={() => handleEditar(servico)}
                          aria-label={t('common.edit')}
                          title={t('common.edit')}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="painel__icon-btn"
                          onClick={() => setServicoParaExcluir(servico)}
                          aria-label={t('common.delete')}
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ServicoFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        servicoParaEditar={servicoParaEditar}
      />

      <AlertDialog
        open={!!servicoParaExcluir}
        onOpenChange={(open) => !open && setServicoParaExcluir(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t('services.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Deseja excluir o serviço <strong>"{servicoParaExcluir?.nome}"</strong>?
              </span>
              <span className="block text-sm bg-muted/50 p-3 rounded-md">
                <strong>Nota:</strong> Se o serviço já foi usado em alguma Ordem de Serviço,
                ele será apenas desativado (exclusão lógica) em vez de excluído permanentemente.
                Isso garante que o histórico das OS antigas não seja afetado.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluirServico.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={excluirServico.isPending}
            >
              {excluirServico.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('services.deleteDialog.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('common.delete')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
